import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Delete, X, Loader2, Landmark } from "lucide-react";

export default function AtmKeypad({ open, onClose, onRedeemed }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { if (open) { setCode(""); setDone(null); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key >= "0" && e.key <= "9") setCode((c) => (c.length < 8 ? c + e.key : c));
      else if (e.key === "Backspace") setCode((c) => c.slice(0, -1));
      else if (e.key === "Enter") submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, code, busy]);

  const press = (d) => setCode((c) => (c.length < 8 ? c + d : c));

  const submit = async () => {
    if (busy || !code) return;
    setBusy(true);
    try {
      const r = await api.redeemAtm(code);
      if (r.ok) {
        setDone(r.message);
        toast.success(r.message, { id: "atm" });
        onRedeemed?.();
        setTimeout(() => onClose?.(), 2600);
      } else {
        setDone(r.message);
        toast.message(r.message, { id: "atm" });
        setTimeout(() => onClose?.(), 2200);
      }
    } catch (e) {
      setShake(true); setTimeout(() => setShake(false), 500);
      setCode("");
      toast.error(e.response?.data?.detail || "The machine rejected that.", { id: "atm" });
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div data-testid="atm-keypad" className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`w-[320px] max-w-full rounded-2xl border border-[#17e9c8]/30 bg-gradient-to-b from-[#0a1420] to-[#070a12] shadow-[0_0_60px_rgba(23,233,200,0.25)] overflow-hidden ${shake ? "atm-shake" : ""}`}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-[#0c1626]">
          <div className="flex items-center gap-2 text-[#17e9c8] font-tactical font-bold text-sm tracking-wider"><Landmark size={15} /> LIFE OS · ATM</div>
          <button data-testid="atm-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>

        <div className="p-4">
          {done ? (
            <div className="py-6 text-center">
              <div className="text-4xl mb-2">💸</div>
              <div className="text-[#17e9c8] font-tactical text-sm">{done}</div>
            </div>
          ) : (
            <>
              <div className="text-center text-[11px] font-mono text-zinc-500 mb-2">Enter access code</div>
              <div data-testid="atm-display" className="h-12 rounded-lg bg-black/60 border border-[#17e9c8]/20 grid place-items-center mb-4">
                <span className="font-mono text-2xl tracking-[0.5em] text-[#17e9c8] pl-[0.5em]">{code ? "•".repeat(code.length) : <span className="text-zinc-700 text-base tracking-normal">● ● ● ●</span>}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["1","2","3","4","5","6","7","8","9"].map((d) => (
                  <button key={d} data-testid={`atm-key-${d}`} onClick={() => press(d)} className="h-12 rounded-lg bg-white/[0.04] border border-white/10 text-zinc-100 font-mono text-lg hover:bg-[#17e9c8]/15 hover:border-[#17e9c8]/40 active:scale-95 transition">{d}</button>
                ))}
                <button data-testid="atm-clear" onClick={() => setCode("")} className="h-12 rounded-lg bg-white/[0.02] border border-white/10 text-zinc-500 hover:text-amber-300 grid place-items-center"><Delete size={18} /></button>
                <button data-testid="atm-key-0" onClick={() => press("0")} className="h-12 rounded-lg bg-white/[0.04] border border-white/10 text-zinc-100 font-mono text-lg hover:bg-[#17e9c8]/15 hover:border-[#17e9c8]/40 active:scale-95 transition">0</button>
                <button data-testid="atm-enter" onClick={submit} disabled={busy || !code} className="h-12 rounded-lg bg-[#17e9c8]/20 border border-[#17e9c8]/50 text-[#17e9c8] font-tactical font-bold uppercase text-xs grid place-items-center disabled:opacity-40 active:scale-95 transition">{busy ? <Loader2 size={16} className="animate-spin" /> : "Enter"}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
